import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@redux/store";
import { addIndustry, removeIndustry } from "@redux/slices/filter";
import { useNavigate } from "react-router";
import {
  ChevronLeft,
  ChevronRight,
  CircleMinus,
  CirclePlus,
  X,
} from "lucide-react";
import { Button } from "@components/ui/button";
import { useState } from "react";
import SimpleBar from "simplebar-react";
import { IndustriesData } from "@assets/mocks/filterData";

import { FilterAtom } from "@/App";
import {useRecoilState } from "recoil";

interface Props {
  handleClose: () => void;
}

const IndustryFilters: React.FC<Props> = ({ handleClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedIndustries = useSelector(
    (state: RootState) => state.filter?.industries?.selectedIndustries
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [dropdowns, setDropdowns] = useState<{ [key: string]: boolean }>({});

  const toggleDropdown = (category: keyof typeof IndustriesData) => {
    setDropdowns((prevState) => ({
      ...prevState,
      [category]: !prevState[category],
    }));
  };

  const handleIndustryToggle = (industry: string) => {
    if (selectedIndustries.includes(industry)) {
      dispatch(removeIndustry(industry));
    } else {
      dispatch(addIndustry(industry));
    }
  };

  const handleParentToggle = (parentCategory: keyof typeof IndustriesData) => {
    const subfields = IndustriesData[parentCategory];

    const isAllSelected = subfields.every((sub: string) =>
      selectedIndustries.includes(sub)
    );

    subfields.forEach((sub: string) => {
      if (isAllSelected) {
        dispatch(removeIndustry(sub));
      } else if (!selectedIndustries.includes(sub)) {
        dispatch(addIndustry(sub));
      }
    });
  };

  const [filter, setFilter] = useRecoilState(FilterAtom);
  const handleClick = (id: string) => {
    console.log("now clicked is " + id);
    // setClickedIds((prevStrings) => [...prevStrings, id]);
    setFilter((prevFilters: string[]) => [...prevFilters, id]); // Append to the atom
    console.log("I am here now");
  };

  return (
    <div className="flex flex-col justify-between h-full p-8">
      <div>
        <div className="flex justify-between items-center mb-3">
          <h4 className="text-xl font-bold flex items-center -ms-2">
            <ChevronLeft
              size={22}
              className="me-3 cursor-pointer"
              onClick={() => navigate(-1)}
            />
            Industry
          </h4>
          <X onClick={handleClose} className="cursor-pointer" />
        </div>

        <div className="flex gap-4 mt-3">
          <div className="w-2/3">
            <div className="relative">
              <input
                className="appearance-none placeholder:text-[#B5B7C0] placeholder:text-sm placeholder:ps-1 border-0 pl-10 border-[#e2e8f0] bg-white shadow-sm  transition-colors rounded-md w-full py-2 px-3 text-gray-800 leading-tight focus:outline-none"
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="absolute left-0 inset-y-0 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 ml-3 text-gray-400 hover:text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="#71717A"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            <SimpleBar style={{ maxHeight: 324 }} className="mt-3">
              <div className="bg-white rounded-lg px-6 py-4">
                {Object.keys(IndustriesData).map((parentCategory) => (
                  <div key={parentCategory}>
                    <div
                      className="flex items-center text-md justify-between font-semibold cursor-pointer pt-3 pb-1 border-b border-[#DBDBDB]"
                      onClick={() =>
                        toggleDropdown(parentCategory as keyof typeof IndustriesData)
                      }
                    >
                      <div className="flex items-center">
                        <ChevronRight
                          size={22}
                          className={
                            dropdowns[parentCategory] ? "rotate-90" : ""
                          }
                        />
                        <h6 className="ms-2">{parentCategory}</h6>
                      </div>

                      <div className="flex">
                        <CircleMinus
                          size={18}
                          className={
                            IndustriesData[parentCategory as keyof typeof IndustriesData].every(
                              (sub: string) => selectedIndustries.includes(sub)
                            )
                              ? "text-slate-400 ms-1"
                              : "text-black rounded-full ms-1"
                          }
                          onClick={() =>
                            handleParentToggle(parentCategory as keyof typeof IndustriesData)
                          }
                        />
                        <CirclePlus
                          size={18}
                          className={
                            IndustriesData[parentCategory as keyof typeof IndustriesData].every(
                              (sub: string) => selectedIndustries.includes(sub)
                            )
                              ? "text-black rounded-full ms-1"
                              : "text-slate-400 ms-1"
                          }
                          onClick={() =>
                            handleParentToggle(parentCategory as keyof typeof IndustriesData)
                          }
                        />
                      </div>
                    </div>

                    {dropdowns[parentCategory] && (
                      <div>
                        {IndustriesData[parentCategory as keyof typeof IndustriesData].map(
                          (subfield: string) => (
                            <div
                              key={subfield}
                              className="flex items-center justify-between text-sm pt-3 pb-1 pl-6 border-b border-[#DBDBDB] cursor-pointer"
                              onClick={() => handleIndustryToggle(subfield)}
                            >
                              <h6 className="ms-1 capitalize">{subfield}</h6>
                              <div className="flex">
                                <CircleMinus
                                  size={18}
                                  className={
                                    selectedIndustries?.includes(subfield)
                                      ? "text-slate-400 ms-1"
                                      : "text-black rounded-full ms-1"
                                  }
                                />
                                <CirclePlus
                                  size={18}
                                  className={
                                    selectedIndustries?.includes(subfield)
                                      ? "text-black rounded-full ms-1"
                                      : "text-slate-400 ms-1"
                                  }
                                />
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </SimpleBar>
          </div>

          <div className="flex flex-col justify-between w-1/3">
            <div className="bg-white rounded-lg px-6 py-4">
              <h4 className="text-lg font-bold flex items-center mb-3">
                Selected Industries
              </h4>
              <SimpleBar style={{maxHeight:370}}>
              {selectedIndustries?.length > 0 ? (
                <ul>
                  {selectedIndustries?.map((industry: string) => (
                    <li key={industry} className="text-[#71717A] text-sm py-1">
                      {industry}
                    </li>
                  ))}
                </ul>
              ) : (
                <h6 className="text-[#71717A] text-sm">
                  No Industries Selected
                </h6>
              )}
              </SimpleBar>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button 
        onClick={() => {
          handleClick("Industry"); // Replace someValue with the actual value you want to pass
          navigate(-1); // Navigate back
        }} 
        className="bg-[#1CB5AF]">Save</Button>
      </div>
    </div>
  );
};

export default IndustryFilters;
